import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { darken, fade } from '@material-ui/core/styles';
import NewAdView, { AdViewPayload } from './NewAdView';
import StatStepper from './StatStepper';
import CreativePreviewDialog from './CreativePreviewDialog';
import AD_TYPE_ICONS from './adTypeIcons';
import { typeLabelOf } from './adTypeOptions';
import { AD } from './adsAgentTokens';
import {
  AlignLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  Link2Icon,
  MapPinIcon,
  TextIcon,
  TypeIcon,
  UserIcon,
  XIcon,
} from './adsAgentIcons';
import {
  DASH,
  currencySymbol,
  fmtAge,
  variantBudget,
  variantCurrency,
  variantGeoLabels,
} from './adVariantFormat';
import { AdTemplateVariant } from './adTemplateIdeas';
import { SIGNUP_URL } from './adsGeneratorApi';

// Read-only port of whatsapp-console-frontend
// `src/pages/AdTemplates/AdVariantCard.js` for the public page: no edits, no
// targeting editor, no budget changes; "Sign up to run ads" takes Run Ad's spot.

const SUMMARY_LINES = 2;
const ICON_HEADER = 16;
const ICON_ACTION = 20;
const ICON_TILE = 17;
const AD_PREVIEW_W = 300;
const AD_PREVIEW_MEDIA_MAX_H = 400;
const DETAILS_MAX_H = 456;
const CAPTION_LINES = 2;
const PREVIEW_DIALOG_W = 1100;
const PREVIEW_DIALOG_VH = 0.9;
const PREVIEW_CONTENT_W = 300;
const PREVIEW_CONTENT_H = 720;

const WHATSAPP_TYPES = ['ctwa', 'whatsapp_status'];

const destinationOf = (variant: AdTemplateVariant): string => {
  if (WHATSAPP_TYPES.includes(variant.adType)) {
    return 'WhatsApp';
  }
  return String(variant.websiteUrl || '').replace(/^https?:\/\//, '');
};

const COPY_ROWS = [
  {
    key: 'destination',
    label: 'Destination',
    icon: <Link2Icon size={ICON_TILE} />,
    display: (variant: AdTemplateVariant) => destinationOf(variant),
  },
  {
    key: 'primaryText',
    label: 'Primary text',
    icon: <AlignLeftIcon size={ICON_TILE} />,
    display: (variant: AdTemplateVariant) => variant.adDescription,
  },
  {
    key: 'headline',
    label: 'Headline',
    icon: <TypeIcon size={ICON_TILE} />,
    display: (variant: AdTemplateVariant) => variant.headline,
  },
  {
    key: 'headlineDescription',
    label: 'Description',
    icon: <TextIcon size={ICON_TILE} />,
    display: (variant: AdTemplateVariant) => variant.headlineDescription,
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    boxShadow: AD.shadowCard,
    borderRadius: AD.radiusLg,
    overflow: 'hidden',
    background: AD.surface,
    position: 'relative',
    fontFamily: AD.fontFamily,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 4,
    padding: '2px 2px 6px 8px',
    background: AD.surface,
    borderBottom: `1px solid ${AD.border}`,
    flexShrink: 0,
  },
  typeLabelBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    minWidth: 0,
  },
  typeLabelText: {
    fontSize: 11,
    fontWeight: 700,
    color: AD.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  split: {
    display: 'flex',
    alignItems: 'stretch',
    flex: 1,
    minHeight: 0,
    [theme.breakpoints.down(700)]: { flexDirection: 'column' },
  },
  previewPane: {
    position: 'relative',
    flex: '1 1 auto',
    maxWidth: '100%',
    minWidth: 0,
    background: AD.surface,
    overflow: 'hidden',
  },
  previewPaneFixed: {
    flex: '0 0 auto',
    maxWidth: 'none',
    [theme.breakpoints.down(700)]: {
      flex: '1 1 auto',
      maxWidth: '100%',
      width: 'auto !important',
    },
  },
  previewPaneSplit: {
    [theme.breakpoints.down(700)]: { flex: '1 1 auto', maxWidth: '100%' },
  },
  previewSlot: {
    width: '100%',
    overflow: 'hidden',
    cursor: 'zoom-in',
  },
  detailsStrip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    height: AD.controlHeight,
    marginBottom: 8,
    padding: '0 10px 0 12px',
    borderRadius: AD.radiusLg,
    border: `1px solid ${AD.border}`,
    background: AD.surface,
    color: AD.text,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: 12.5,
    transition: 'border-color 150ms ease, color 150ms ease',
    '&:hover': {
      borderColor: AD.cta,
      color: AD.cta,
      background: fade(AD.cta, 0.06),
    },
  },
  detailsChevron: {
    display: 'inline-flex',
    flexShrink: 0,
    transition: 'transform 150ms ease',
  },
  detailsChevronOpen: { transform: 'rotate(90deg)' },
  detailsPane: {
    flex: '1 1 0',
    minWidth: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxHeight: DETAILS_MAX_H,
    animation: '$detailsPaneOpen 220ms ease-out',
  },
  detailsPaneFixed: {
    flex: '0 0 auto',
    [theme.breakpoints.down(700)]: {
      flex: '1 1 auto',
      width: 'auto !important',
      maxWidth: '100%',
    },
  },
  detailsScroll: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  '@keyframes detailsPaneOpen': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  detailsPaneInner: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    width: '100%',
    minWidth: 0,
    animation: '$detailsPaneSlideIn 220ms ease-out',
  },
  '@keyframes detailsPaneSlideIn': {
    from: { transform: 'translateX(-24px)' },
    to: { transform: 'translateX(0)' },
  },
  tile: {
    width: 30,
    height: 30,
    borderRadius: AD.radiusLg,
    color: AD.cta,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  section: { padding: '14px 12px 0 6px' },
  row: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    '& + &': { marginTop: 12 },
  },
  rowBody: { minWidth: 0, flex: 1, paddingTop: 1 },
  rowLabel: {
    fontFamily: AD.fontFamily,
    fontSize: 11,
    fontWeight: 700,
    color: AD.text,
    lineHeight: 1,
    marginBottom: 2,
  },
  rowValue: {
    fontFamily: AD.fontFamily,
    fontSize: 11,
    color: AD.textMuted,
    lineHeight: 1.45,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
  },
  rowValueClamp: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: SUMMARY_LINES,
    overflow: 'hidden',
  },
  moreLink: {
    display: 'inline-block',
    marginTop: 4,
    fontSize: 11,
    fontWeight: 700,
    color: AD.cta,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { textDecoration: 'underline' },
  },
  footer: {
    padding: '11px 14px 14px',
    flexShrink: 0,
  },
  statRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 8,
  },
  budgetRow: { marginBottom: 8 },
  detailsStripRow: {
    flex: '1 1 0',
    minWidth: 0,
    marginBottom: 0,
  },
  runSlot: {
    flex: '0.72 1 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'flex-end',
  },
  runButton: {
    width: '100%',
    height: AD.controlHeight,
    background: AD.cta,
    color: AD.surface,
    borderRadius: AD.radiusLg,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: 12.5,
    padding: '9px 10px',
    whiteSpace: 'nowrap',
    '&:hover': { background: darken(AD.cta, 0.15) },
  },
}));

type Classes = ReturnType<typeof useStyles>;

const TargetRow = ({
  classes,
  icon,
  label,
  children,
}: {
  classes: Classes;
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}): React.ReactElement => (
  <Box className={classes.row}>
    <Box className={classes.tile}>{icon}</Box>
    <Box className={classes.rowBody}>
      <Typography className={classes.rowLabel}>{label}</Typography>
      {children}
    </Box>
  </Box>
);

const SummaryText = ({
  classes,
  text,
  open,
  onToggle,
}: {
  classes: Classes;
  text: string;
  open: boolean;
  onToggle: () => void;
}): React.ReactElement => {
  const textRef = useRef<HTMLElement>(null);
  const [clipped, setClipped] = useState(false);

  useEffect(() => {
    if (open) {
      return undefined;
    }
    const measure = () => {
      const el = textRef.current;
      if (el) {
        setClipped(el.scrollHeight - el.clientHeight > 1);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [text, open]);

  return (
    <>
      <Typography
        ref={textRef}
        className={`${classes.rowValue} ${open ? '' : classes.rowValueClamp}`}
      >
        {text}
      </Typography>
      {clipped || open ? (
        <span
          className={classes.moreLink}
          role="button"
          tabIndex={0}
          onClick={onToggle}
          onKeyDown={(e) => e.key === 'Enter' && onToggle()}
        >
          {open ? 'See less' : 'See more'}
        </span>
      ) : null}
    </>
  );
};

const AdVariantCard = (props: Props): React.ReactElement => {
  const {
    variant,
    expanded,
    previewWidth,
    detailsWidth,
    onToggleExpand,
  } = props;
  const classes = useStyles();

  const previewRef = useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [mediaOffset, setMediaOffset] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [adViewOpen, setAdViewOpen] = useState(false);
  const [adScale, setAdScale] = useState(1);
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

  const typeLabel = typeLabelOf(variant.adType);
  const geoLabels = variantGeoLabels(variant);
  const age = fmtAge(variant);
  const { amount, isLifetime } = variantBudget(variant);
  const symbol = currencySymbol(variantCurrency(variant));
  const websiteUrl = variant.websiteUrl || '';

  const adViewPayload: AdViewPayload = {
    adCaption: variant.adDescription,
    adHeadline: variant.headline,
    adHeadlineDescription: variant.headlineDescription,
    mediaType: 'IMAGE',
    imageFile: variant.imageUrl ? { publicUrl: variant.imageUrl } : null,
    videoFile: null,
    thumbnailUrl: variant.imageUrl || null,
    ctaButton: variant.callToAction || variant.ctaButton || 'LEARN_MORE',
    adType: { value: variant.adType || 'click_to_whatsapp' },
    pageDetails: { picture: '', pageName: 'Your Business', whatsappNumber: '' },
    previewLink: null,
  };

  useEffect(() => {
    const el = previewRef.current;
    if (!el) {
      return undefined;
    }
    const measure = () => {
      const width = el.clientWidth;
      if (width > 0) {
        setPreviewScale(width / AD_PREVIEW_W);
      }
      const media = el.querySelector('img, video');
      if (!media) {
        return;
      }
      const top = media.getBoundingClientRect().top - el.getBoundingClientRect().top;
      setMediaOffset(top > 0 ? Math.round(top) : 0);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!adViewOpen) {
      return undefined;
    }
    const compute = () => {
      const paperW = Math.min(PREVIEW_DIALOG_W, window.innerWidth * 0.94);
      const availW = paperW - 32;
      const availH = window.innerHeight * PREVIEW_DIALOG_VH - 56;
      setAdScale(Math.min(availW / PREVIEW_CONTENT_W, availH / PREVIEW_CONTENT_H));
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [adViewOpen]);

  const toggleRow = (key: string) =>
    setOpenRows((current) => ({ ...current, [key]: !current[key] }));

  return (
    <Card className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.typeLabelBox}>
          {AD_TYPE_ICONS[variant.adType]}
          <span className={classes.typeLabelText}>{typeLabel}</span>
        </Box>
        <Box className={classes.cardActions}>
          <Tooltip title="Preview this ad">
            <IconButton size="small" onClick={() => setAdViewOpen(true)}>
              <EyeIcon size={ICON_HEADER} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box className={classes.split}>
        <Box
          className={`${classes.previewPane} ${expanded ? classes.previewPaneSplit : ''} ${
            previewWidth ? classes.previewPaneFixed : ''
          }`}
          style={previewWidth ? { width: previewWidth } : undefined}
        >
          <div
            className={classes.previewSlot}
            ref={previewRef}
            onClick={() => setPreviewOpen(true)}
          >
            <Box style={{ width: AD_PREVIEW_W, zoom: previewScale }}>
              <NewAdView
                payload={adViewPayload}
                websiteUrl={websiteUrl}
                isFromFlowKeyword
                hideInAppBrowserNote
                hidePageHeader
                hideDestination
                flat
                footerBg={AD.surface}
                captionClamp={CAPTION_LINES}
                mediaMaxHeight={AD_PREVIEW_MEDIA_MAX_H}
              />
            </Box>
          </div>
        </Box>

        {expanded ? (
          <Box
            className={`${classes.detailsPane} ${detailsWidth ? classes.detailsPaneFixed : ''}`}
            style={detailsWidth ? { width: detailsWidth } : undefined}
          >
            <Box className={classes.detailsPaneInner}>
              <Box className={classes.detailsScroll}>
                <Box
                  className={classes.section}
                  style={{ paddingTop: mediaOffset, paddingBottom: 14 }}
                >
                  <TargetRow classes={classes} icon={<MapPinIcon size={ICON_TILE} />} label="Locations">
                    {geoLabels.length ? (
                      <SummaryText
                        classes={classes}
                        text={geoLabels.join(', ')}
                        open={Boolean(openRows.geo)}
                        onToggle={() => toggleRow('geo')}
                      />
                    ) : (
                      <Typography className={classes.rowValue}>{DASH}</Typography>
                    )}
                  </TargetRow>

                  <TargetRow classes={classes} icon={<UserIcon size={ICON_TILE} />} label="Age">
                    <Typography className={classes.rowValue}>
                      {age !== DASH ? `${age} years` : DASH}
                    </Typography>
                  </TargetRow>

                  {COPY_ROWS.map((row) => (
                    <TargetRow key={row.key} classes={classes} icon={row.icon} label={row.label}>
                      <SummaryText
                        classes={classes}
                        text={row.display(variant) || DASH}
                        open={Boolean(openRows[row.key])}
                        onToggle={() => toggleRow(row.key)}
                      />
                    </TargetRow>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>

      <Box
        className={classes.footer}
        style={
          expanded && previewWidth
            ? { width: previewWidth, boxSizing: 'border-box' }
            : undefined
        }
      >
        <Box className={classes.budgetRow}>
          <StatStepper
            label="Budget"
            value={`${symbol}${amount.toLocaleString('en-IN')}`}
            unit={isLifetime ? 'total' : '/ day'}
          />
        </Box>

        <Box className={classes.statRow}>
          <Button
            className={`${classes.detailsStrip} ${classes.detailsStripRow}`}
            onClick={onToggleExpand}
            aria-expanded={expanded}
          >
            Targeting &amp; copy
            <span
              className={`${classes.detailsChevron} ${
                expanded ? classes.detailsChevronOpen : ''
              }`}
            >
              <ChevronRightIcon size={ICON_HEADER} />
            </span>
          </Button>

          <Box className={classes.runSlot}>
            <Button
              className={classes.runButton}
              component="a"
              href={SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Sign up to run ads
            </Button>
          </Box>
        </Box>
      </Box>

      <CreativePreviewDialog
        open={previewOpen}
        imageUrl={variant.imageUrl}
        onClose={() => setPreviewOpen(false)}
      />

      <Dialog
        open={adViewOpen}
        onClose={() => setAdViewOpen(false)}
        maxWidth={false}
        PaperProps={{
          style: {
            width: PREVIEW_DIALOG_W,
            maxWidth: '94vw',
            height: `${PREVIEW_DIALOG_VH * 100}vh`,
            borderRadius: AD.radiusLg,
            background: AD.borderSubtle,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            background: AD.surface,
            borderBottom: `1px solid ${AD.border}`,
            flexShrink: 0,
          }}
        >
          <Typography
            style={{ fontFamily: AD.fontFamily, fontSize: 14, fontWeight: 700, color: AD.text }}
          >
            Ad Preview
          </Typography>
          <IconButton size="small" onClick={() => setAdViewOpen(false)}>
            <XIcon size={ICON_ACTION} />
          </IconButton>
        </div>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '20px 16px 16px',
            background: AD.borderSubtle,
          }}
        >
          <div style={{ zoom: adScale }}>
            <NewAdView
              payload={adViewPayload}
              websiteUrl={websiteUrl}
              isFromFlowKeyword={false}
              hideInAppBrowserNote
            />
          </div>
        </div>
      </Dialog>
    </Card>
  );
};

interface Props {
  variant: AdTemplateVariant;
  expanded: boolean;
  previewWidth: number;
  detailsWidth: number;
  onToggleExpand: () => void;
}

export default AdVariantCard;
